import { Body, H4 } from '@casavo/habitat'
import useTranslation from 'next-translate/useTranslation'
import React, { Dispatch, SetStateAction } from 'react'

import { LabelWithCheck } from '@/components/LabelWithCheck'
import { PriorityBadge } from '@/components/PriorityCardBadge'
import { Translated } from '@/components/Translated'
import { CasavoAdvantage } from '@/containers/ListingDetail/CasavoAdvantage'
import { detailContactBox } from '@/containers/ListingDetail/styles.css'
import { useAuthentication } from '@/context/AuthProvider'
import { useCurrentTenant } from '@/context/TenantProvider'
import { Button } from '@/design-lib/components/Button'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { TENANTS } from '@/lib/tenants/constants'
import { paddingBottom } from '@/shared-styles/styles.css'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick } from '@/utils/tracking/eventsHandlers'

import { AlreadyBookedBanner } from '../../AlreadyBookedBanner/AlreadyBookedBanner'
import { ButtonFeedback } from '../../ButtonFeedback'
import { RequestVisitCTA } from '../RequestVisit'

type Props = {
  advantages: ReadonlyArray<CasavoAdvantage>
  disableVisitCTA: boolean
  handleRequestVisitCtaBuyerInfo: VoidFunction
  hasBuyerInfo: boolean
  setShowToastError: Dispatch<SetStateAction<boolean>>
  showAlreadyBookedBanner: boolean
  thankYouPageUrl: string
  transactionId: string
}

export const FloatingBanner: React.FC<Props> = ({
  advantages,
  disableVisitCTA,
  handleRequestVisitCtaBuyerInfo,
  hasBuyerInfo,
  setShowToastError,
  showAlreadyBookedBanner,
  thankYouPageUrl,
  transactionId,
}) => {
  const { t } = useTranslation()
  const tenant = useCurrentTenant()
  const { isLoading, user } = useAuthentication()
  const handleClickRequestVisitCta = () =>
    user && !isLoading
      ? handleClick(GTMEventCategory.PriorityDetail, 'FloatingBanner_RequestVisit')
      : handleClick(GTMEventCategory.LockedPriorityDetail, 'RequestVisit')

  const RequestVisitCta = hasBuyerInfo ? (
    <Button
      fullWidth
      disabled={disableVisitCTA}
      label={t('priority:Priority_DetailPage_FloatingBanner_CTA')}
      loading={user && disableVisitCTA}
      minWidth={200}
      styleVariant="primary"
      onClick={handleRequestVisitCtaBuyerInfo}
    />
  ) : (
    <RequestVisitCTA
      gtmTouchPoint="FromVisitLockedPriority"
      handleCtaClick={handleClickRequestVisitCta}
      setShowToastError={setShowToastError}
      thankYouPageUrl={thankYouPageUrl}
      transactionId={transactionId}
    />
  )

  return (
    <div className={detailContactBox}>
      <PriorityBadge />
      <H4>{t('priority:Priority_DetailPage_FloatingBanner_Title')}</H4>

      <div className={paddingBottom.l}>
        {tenant === TENANTS.casavo &&
          advantages.map((advantage) => (
            <LabelWithCheck key={advantage.titleTranslationKey} tenant={tenant}>
              {t(advantage.titleTranslationKey)}
            </LabelWithCheck>
          ))}
        {tenant === TENANTS.ucsc && (
          <Body size="s">
            <Translated i18Key={'unicredit:Priority_DetailPage_FloatingBanner_Desciption'} />
          </Body>
        )}
      </div>

      {showAlreadyBookedBanner ? (
        <AlreadyBookedBanner className={sprinkles({ marginBottom: 'm' })} />
      ) : (
        <>
          {RequestVisitCta}
          {tenant === TENANTS.casavo && (
            <ButtonFeedback className={sprinkles({ marginBottom: 'xl', marginTop: 'm' })} />
          )}
        </>
      )}

      {tenant === TENANTS.casavo && (
        <div style={{ textAlign: 'center' }}>
          <Body color={'root.neutral.400'} size="xs">
            {showAlreadyBookedBanner
              ? t('priority:PriorityDetail_FloatingBanner_Booked_Description')
              : t('priority:PriorityDetail-FloatingBanner-FeedbackDescription')}
          </Body>
        </div>
      )}
    </div>
  )
}
